import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { OrderDirection } from 'webapp/enums/enums';

import type { UnusedItemModel } from 'webapp/components/metrics/unused/popup';

import CustomQuery from 'webapp/models-custom/custom-query';
import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import HorizonMetric from 'webapp/models-custom/horizon-metric';
import Script from 'webapp/models-custom/script';

export enum UnusedMetricsFilterId {
	DATA_SOURCE = 1,
	CREATOR = 2,
	TYPE = 3,
}

export enum MetricType {
	ALL = 0,
	CUSTOM = 1,
	CALCULATED = 2,
}

export const settingsNamePrefix = 'unusedMetricsFilterSettings';

export interface UnusedMetricsFilterSortSettings extends FilterSortSettings<UnusedItemModel> {
	initialTypeFilter?: MetricType;
}

export default class FilterSortUnusedMetrics extends FilterSort<UnusedItemModel> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;

	// custom search that handles both CustomQuery and Script
	override searchProps = {
		queryName: 1, // CustomQuery
		description: 1, // Script
		metricName: 1, // HorizonMetric
	};

	@tracked override sortProp = 'created';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.DESC;

	override settingsNamePrefix = settingsNamePrefix;
	override trackingObjectName = 'unused-metric';

	private initialTypeFilter?: MetricType;

	constructor(options: UnusedMetricsFilterSortSettings) {
		super(options);

		this.initialTypeFilter = options.initialTypeFilter;

		const selectedFilters = this.readSavedSelectedFilters();

		// Data source filter
		this.createFilter<string>({
			id: UnusedMetricsFilterId.DATA_SOURCE,
			label: 'Data source',
			name: 'data source',
			selected: selectedFilters[UnusedMetricsFilterId.DATA_SOURCE] as string[] | undefined,
			stringValues: true,
			isMultiSelect: false,
			getOptions: () => this.datasourceOptions,
			filterFn: (item, selected) => {
				const selectedArray = Array.isArray(selected) ? selected : [selected];

				if (selectedArray.includes('all')) {
					return true;
				}

				const itemSourceKeys = this.getSourceKeysForItem(item);
				return selectedArray.some((key) => itemSourceKeys.includes(key));
			},
		});

		// Creator filter
		this.createFilter<number>({
			id: UnusedMetricsFilterId.CREATOR,
			label: 'Created by',
			name: 'creator',
			selected: selectedFilters[UnusedMetricsFilterId.CREATOR] as number[] | undefined,
			isMultiSelect: false,
			getOptions: () => this.creatorOptions,
			filterFn: (item, selected) => {
				const selectedArray = Array.isArray(selected) ? selected : [selected];
				if (selectedArray.includes(-1)) {
					return !item.isMine;
				} else if (selectedArray.includes(0)) {
					return true;
				} else {
					return selectedArray.some((s) => s === item.createdById);
				}
			},
		});

		// Type filter (Custom vs Calculated)
		const typeSelected =
			this.initialTypeFilter !== undefined
				? [this.initialTypeFilter]
				: (selectedFilters[UnusedMetricsFilterId.TYPE] as number[] | undefined);

		this.createFilter<number>({
			id: UnusedMetricsFilterId.TYPE,
			label: 'Type',
			name: 'type',
			selected: typeSelected,
			isMultiSelect: false,
			getOptions: () => this.typeOptions,
			filterFn: (item, selected) => {
				const selectedValue = Array.isArray(selected) ? selected[0] : selected;
				if (selectedValue === MetricType.ALL) {
					return true;
				}
				if (selectedValue === MetricType.CUSTOM) {
					return item instanceof CustomQuery || item instanceof HorizonMetric;
				}
				if (selectedValue === MetricType.CALCULATED) {
					return item instanceof Script;
				}
				return true;
			},
		});
	}

	get typeOptions(): FilterOption<number>[] {
		return [
			{ id: MetricType.ALL, text: 'All' },
			{ id: MetricType.CUSTOM, text: 'Built' },
			{ id: MetricType.CALCULATED, text: 'Calculated' },
		];
	}

	@cached
	get datasourceOptions(): FilterOption<string>[] {
		const sourceKeyCounts: Record<string, number> = {};

		this.allItems.forEach((item) => {
			const keys = this.getSourceKeysForItem(item);
			keys.forEach((key) => {
				sourceKeyCounts[key] = (sourceKeyCounts[key] || 0) + 1;
			});
		});

		const list = this.mmSourceTypesService.getSourceTypesListWithCount(
			Object.keys(sourceKeyCounts),
		);

		list.unshift({ id: 'all', text: 'All' });

		return list;
	}

	@cached
	get creatorOptions(): FilterOption<number>[] {
		const allItemCountByCreator: Record<number, number> = {};
		this.allItems.forEach((item) => {
			const createdById = item.createdById;
			if (!allItemCountByCreator[createdById]) {
				allItemCountByCreator[createdById] = 0;
			}
			allItemCountByCreator[createdById]++;
		});

		return this.mmUsersService.getFilterOptionsForUsers(allItemCountByCreator);
	}

	private getSourceKeysForItem(item: UnusedItemModel): string[] {
		const keys: string[] = [];

		if (item instanceof Script) {
			item.usedSources.forEach((source) => {
				const sourceType = this.mmSourceTypesService.findModelById(source.type);
				if (sourceType?.key) {
					keys.push(sourceType.key);
				}
			});
		} else if (item instanceof CustomQuery) {
			const sourceType = item.sourceInfo;
			if (sourceType?.key) {
				keys.push(sourceType.key);
			}
		} else if (item instanceof HorizonMetric) {
			item.usedSources.forEach((source) => {
				const sourceTypeKey = source.sourceType?.key;
				if (sourceTypeKey) {
					keys.push(sourceTypeKey);
				}
			});
		}

		return keys;
	}
}
